<script setup lang="ts">
import { QDialog } from 'quasar'
import { computed, useSlots } from 'vue'
import { Color } from '@previo/codebooks'
import { $t } from '@previo/core'
import PCard from '../card/PCard.vue'
import { PCardRound, PCardSize } from '../card/types'
import PBtn from '../btn/PBtn.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PSpaceSize } from '../space/types'
import { PTabsAlign } from '../tabs/types'
import { PBtnType } from '../btn/types'
import { PMaxLines } from '../typography/types'
import PSpace from '../space/PSpace.vue'
import PStepper from '../stepper/PStepper.vue'
import PDivider from '../divider/PDivider.vue'
import PStepperStep from '../stepper/PStepperStep.vue'
import { PStepperStatus } from '../stepper/types'
import PTab from '../tabs/PTab.vue'
import PTabs from '../tabs/PTabs.vue'
import PTextTitle1 from '../typography/title/PTextTitle1.vue'
import {
  PDialogPosition,
  type PFloatingDialogStep,
  type PFloatingDialogTab,
  PFloatingPanelDialogVariant,
} from './types'

const model = defineModel<boolean>()
const selectedTab = defineModel<string>('selectedTab')
const selectedStep = defineModel<string>('selectedStep')

type Props = {
  title?: null | string
  variant?: PFloatingPanelDialogVariant
  tabs?: PFloatingDialogTab[]
  steps?: PFloatingDialogStep[]

  primaryBtn?: null | string
  primaryBtnDisable?: boolean
  primaryBtnLoading?: boolean
  primaryBtnVisible?: boolean

  secondaryBtn?: null | string
  secondaryBtnColor?: Color.PRIMARY | Color.POSITIVE
  secondaryBtnDisable?: boolean
  secondaryBtnLoading?: boolean
  secondaryBtnVisible?: boolean

  tertiaryBtn?: null | string
  tertiaryBtnDisable?: boolean
  tertiaryBtnLoading?: boolean
  tertiaryBtnVisible?: boolean

  persistent?: boolean
  large?: boolean

  cy: string
}

type Emits = {
  (e: 'primary', evt: Event): void
  (e: 'secondary', evt: Event): void
  (e: 'tertiary', evt: Event): void
  (e: 'close', evt: Event): void
  (e: 'nextStep', currentStepKey: string, next: () => void): void
  (e: 'prevStep', currentStepKey: string, prev: () => void): void
}

const props = withDefaults(defineProps<Props>(), {
  variant: undefined,
  tabs() {
    return []
  },
  steps() {
    return []
  },
  primaryBtnDisable: false,
  primaryBtnLoading: false,
  primaryBtnVisible: true,
  secondaryBtnColor: Color.PRIMARY,
  secondaryBtnDisable: false,
  secondaryBtnLoading: false,
  secondaryBtnVisible: true,
  tertiaryBtnDisable: false,
  tertiaryBtnLoading: false,
  tertiaryBtnVisible: true,
  persistent: false,
  large: false,
  end: false,
})
const emits = defineEmits<Emits>()

const ds = usePrevioDS()
const slots = useSlots()

const position = computed<PDialogPosition>(() => {
  return ds.screen().isMobile ? PDialogPosition.BOTTOM : PDialogPosition.END
})

const bottomEndRound = computed<PCardRound>(() => {
  return ds.screen().isMobile ? PCardRound.ZERO : PCardRound.LG
})

const bottomStartRound = computed<PCardRound>(() => {
  return ds.screen().isMobile ? PCardRound.ZERO : PCardRound.LG
})

const isStepsVariant = computed<boolean>(() => {
  return props.variant === PFloatingPanelDialogVariant.STEPS
})

const activeStepIndex = computed<number>(() => {
  return props.steps.findIndex((step) => step.key === selectedStep.value)
})

const isFirstStep = computed<boolean>(() => {
  return activeStepIndex.value <= 0
})

const isLastStep = computed<boolean>(() => {
  return activeStepIndex.value === props.steps.length - 1
})

const stepStatus = (index: number): PStepperStatus => {
  if (index < activeStepIndex.value) {
    return PStepperStatus.COMPLETED
  }

  if (index === activeStepIndex.value) {
    return PStepperStatus.ACTIVE
  }

  return PStepperStatus.DEFAULT
}

const hasStepper = computed<boolean>(() => {
  return isStepsVariant.value && props.steps.length !== 0
})

const primaryBtnText = computed<null | string>(() => {
  if (isStepsVariant.value && !isLastStep.value) {
    return $t('Next')
  }

  return props.primaryBtn ?? null
})

const tertiaryBtnText = computed<null | string>(() => {
  if (isStepsVariant.value) {
    if (ds.screen().isMobile) {
      return null
    }

    if (!isFirstStep.value) {
      return $t('Back')
    }
  }

  return props.tertiaryBtn ?? null
})

const tertiaryBtnIcon = computed<null | string>(() => {
  if (isStepsVariant.value) {
    if (ds.screen().isMobile && !isFirstStep.value) {
      return 'arrow-left'
    }

    if (!isFirstStep.value) {
      return null
    }
  }

  return null
})

const hasPrimaryBtn = computed<boolean>(() => {
  return !!primaryBtnText.value && props.primaryBtnVisible
})

const hasSecondaryBtn = computed<boolean>(() => {
  return !!props.secondaryBtn && props.secondaryBtnVisible
})

const hasTertiaryBtn = computed<boolean>(() => {
  return (!!tertiaryBtnText.value || !!tertiaryBtnIcon.value) && props.tertiaryBtnVisible
})

const hasButton = computed<boolean>(() => {
  return hasPrimaryBtn.value || hasSecondaryBtn.value || hasTertiaryBtn.value
})

const hasButtonGrow = computed<boolean>(() => {
  return ds.screen().isMobile
})

const nextStep = (e: Event) => {
  const target = props.steps[activeStepIndex.value + 1]

  if (target) {
    selectedStep.value = target.key
    return
  }

  emits('primary', e)
}

const prevStep = () => {
  const target = props.steps[activeStepIndex.value - 1]

  if (target) {
    selectedStep.value = target.key
  }
}

const onPrimaryClick = (e: Event) => {
  if (!isStepsVariant.value || !selectedStep.value) {
    emits('primary', e)
    return
  }

  emits('nextStep', selectedStep.value, () => nextStep(e))
}

const onTertiaryClick = (e: Event) => {
  if (!isStepsVariant.value || isFirstStep.value || !selectedStep.value) {
    emits('tertiary', e)
    return
  }

  emits('prevStep', selectedStep.value, prevStep)
}

const onClose = (e: Event) => {
  model.value = false
  emits('close', e)
}
</script>

<template>
  <q-dialog
    v-model="model"
    :position="position"
    :transition-duration="200"
    transition-show="fade"
    transition-hide="fade"
    :persistent="persistent"
    maximized
    @hide="onClose"
  >
    <div class="p-display-flex">
      <p-card
        class="ds-floating-panel-dialog p-display-flex p-flex-direction-column"
        :class="[
          large ? 'ds-floating-panel-dialog__large' : 'ds-floating-panel-dialog__small',
          { 'ds-floating-panel-dialog__remove-bottom-padding': !hasButton },
        ]"
        :pa-mobile="PCardSize.SM"
        :color="Color.WHITE"
        :round="PCardRound.LG"
        :round-bottom-start="bottomStartRound"
        :round-bottom-end="bottomEndRound"
        :data-cy="cy"
      >
        <template v-if="!!slots['header']">
          <slot name="header" />
        </template>
        <div v-else>
          <div class="p-display-flex p-flex-center p-no-wrap">
            <p-text-title1 :color="Color.GREY_600" :text="title" :max-lines="PMaxLines.ONE" />
            <p-space :mobile="PSpaceSize.GROW" />
            <p-space :mobile="PSpaceSize.LG" />
            <p-btn :type="PBtnType.OUTLINE" icon-end="xmark-large" cy="close" @click="onClose" />
          </div>
        </div>
        <p-space v-if="hasStepper" :mobile="PSpaceSize.SM" vertical />

        <template v-if="hasStepper">
          <p-stepper scrollable>
            <p-stepper-step
              v-for="(step, index) in steps"
              :key="step.key"
              :status="stepStatus(index)"
              :icon="step.icon"
              :text="step.label"
              :description="step.description"
            />
          </p-stepper>
        </template>

        <template v-else-if="tabs.length">
          <p-tabs v-model="selectedTab" :align="PTabsAlign.LEFT" scrollable>
            <p-tab v-for="tab in tabs" :key="tab.key" :label="tab.label" :name="tab.key" />
          </p-tabs>

          <p-divider />
        </template>

        <div class="ds-floating-panel-dialog__content p-overflow-auto">
          <slot />
        </div>

        <div v-if="hasButton" class="ds-floating-panel-dialog__buttons" data-menu-boundary>
          <p-space :mobile="PSpaceSize.SM" :laptop="PSpaceSize.MD" vertical />
          <div class="p-display-flex p-no-wrap">
            <p-space :mobile="PSpaceSize.GROW" />

            <template v-if="hasTertiaryBtn">
              <p-btn
                :text="tertiaryBtnText"
                :color="Color.PRIMARY"
                :icon-end="ds.screen().isLaptopAndLarge || isStepsVariant ? tertiaryBtnIcon : null"
                :disable="tertiaryBtnDisable"
                :loading="tertiaryBtnLoading"
                :type="PBtnType.OUTLINE"
                :grow="hasButtonGrow && !isStepsVariant"
                cy="tertiary"
                @click="onTertiaryClick"
              />
              <p-space :mobile="PSpaceSize.SM" />
            </template>

            <template v-if="hasSecondaryBtn">
              <p-btn
                :text="secondaryBtn"
                :color="secondaryBtnColor"
                :disable="secondaryBtnDisable"
                :loading="secondaryBtnLoading"
                :type="PBtnType.SECONDARY"
                :grow="hasButtonGrow"
                cy="secondary"
                @click="(e) => emits('secondary', e)"
              />
              <p-space :mobile="PSpaceSize.SM" />
            </template>

            <p-btn
              v-if="hasPrimaryBtn"
              :text="primaryBtnText"
              :color="Color.POSITIVE"
              :disable="primaryBtnDisable"
              :loading="primaryBtnLoading"
              :grow="hasButtonGrow"
              cy="primary"
              @click="onPrimaryClick"
            />
          </div>
        </div>
      </p-card>
    </div>
  </q-dialog>
</template>

<style scoped lang="scss">
@import '../../assets/scss/shared/mixins';
@import '../../assets/scss/shared/variables/ds.spacing';

.ds-floating-panel-dialog {
  @include safe-area-padding(left, $space-sm-mobile);
  @include safe-area-padding(right, $space-sm-mobile);
  min-width: 0;

  @include laptopAndLarge() {
    margin: $space-sm-mobile;
    max-width: calc(100dvw - #{$space-sm-mobile} * 2);
  }

  @include mobile() {
    height: calc(100dvh - $space-xxl-mobile);
  }

  &__small {
    width: 640px;

    @include mobile() {
      width: 100%;
    }
  }

  &__large {
    width: 1896px;

    @include mobile() {
      width: 100%;
    }
  }

  &__remove-bottom-padding {
    @include safe-area-padding(bottom);
  }

  &__content {
    flex: 1 1 auto;
    min-width: 0;
    min-height: 0;

    @include mobile() {
      padding-top: var(--space-cards-mobile);
    }

    @include laptop() {
      padding-top: var(--space-cards-laptop);
    }

    @include desktop() {
      padding-top: var(--space-cards-desktop);
    }
  }

  &__buttons {
    @include safe-area-padding(bottom);
  }
}
</style>
